import React from 'react';

import ToastVariant from '@lib/core/src/enums/ToastVariant';
import palette from '@lib/core/src/theme/palette';
import CloseIcon from '@mui/icons-material/Close';
import { Typography } from '@mui/material';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';

import { CalendarMeetingInfoDto } from '@/api/generated';

export const MAX_SELECTED_MEETINGS = 10;

interface MeetingHubActions {
  setSelectedMeetings: (meetings: CalendarMeetingInfoDto[]) => void;
  addRemoveSelectedMeeting: (meeting: CalendarMeetingInfoDto) => void;
  clearSelectedMeetings: () => void;
}

interface SelectedMeetingsStore {
  selectedMeetings: CalendarMeetingInfoDto[];
  actions: MeetingHubActions;
}

export const useSelectedMeetingsStore = create<SelectedMeetingsStore>()(
  devtools(
    immer((set, get) => ({
      selectedMeetings: [],
      actions: {
        setSelectedMeetings: (meetings: CalendarMeetingInfoDto[]) =>
          set(draft => {
            // eslint-disable-next-line no-param-reassign
            draft.selectedMeetings = meetings;
          }),
        addRemoveSelectedMeeting: (meeting: CalendarMeetingInfoDto) => {
          set(draft => {
            const meetingIndex = draft.selectedMeetings.findIndex(m => m.id === meeting.id);
            if (meetingIndex === -1) {
              if (draft.selectedMeetings.length >= MAX_SELECTED_MEETINGS) {
                enqueueSnackbar({
                  variant: 'custom',
                  anchorOrigin: { vertical: 'top', horizontal: 'center' },
                  customProps: {
                    sx: {
                      borderRadius: '48px',
                      backgroundColor: palette.black
                    },
                    caption: React.createElement(
                      Typography,
                      { color: palette.white, fontSize: '14px', fontWeight: 600 },
                      `You can select up to ${MAX_SELECTED_MEETINGS} meetings at a time. To add a new one, please deselect an existing meeting first.`
                    ),
                    variant: ToastVariant.INFO,
                    cta: {
                      label: React.createElement(CloseIcon, {
                        sx: { color: palette.white, width: '14px', height: '14px' }
                      }),
                      onClick: () => {
                        closeSnackbar();
                      }
                    }
                  }
                });
                return;
              }
              // eslint-disable-next-line no-param-reassign
              draft.selectedMeetings.push(meeting);
            } else {
              closeSnackbar();
              // eslint-disable-next-line no-param-reassign
              draft.selectedMeetings.splice(meetingIndex, 1);
            }
          });
        },
        clearSelectedMeetings: () =>
          set(draft => {
            // eslint-disable-next-line no-param-reassign
            draft.selectedMeetings = [];
          })
      }
    })),
    { name: 'meetingHubStore' }
  )
);

export const useSelectedMeetings = () => useSelectedMeetingsStore(state => state.selectedMeetings);
export const useSelectedMeetingsActions = () => useSelectedMeetingsStore(state => state.actions);

export const useIsMeetingSelected = (meetingId: string) =>
  useSelectedMeetingsStore(state => state.selectedMeetings.some(m => m.id === meetingId));
